import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Layout from "../../../components/layout/Layout";
import PageHeader from "../../../components/layout/PageHeader";
import { useCompanies } from "../../../api/queries/organizationQueries";
import {
  useNotificationCatalog,
  useNotificationSettings,
  useResetNotificationSettings,
  useSaveNotificationSettings,
} from "../../../api/queries/notificationQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import usePermissions from "../../../hooks/usePermissions";
import { getApiErrorMessage } from "../../../utils/apiError";
import {
  RECIPIENT_LABEL,
  SOURCE_LABEL,
  groupTypes,
  isEnabled,
  probationProblems,
  setEnabled,
  toPayload,
  toggleIn,
} from "./notificationSettingsFormat";
import "../admin.css";

const EMPTY = [];
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const messagesOf = (err, fallback) => {
  const m = err?.response?.data?.message;
  return Array.isArray(m) ? m : [getApiErrorMessage(err, fallback)];
};

/** On/off switch row for one notification type. */
const TypeRow = ({ t, on, disabled, onChange }) => (
  <li className="ns-row">
    <div className="min-w-0">
      <div className="fw-semibold fs-085">{t.label || t.type}</div>
      {t.recipient && (
        <div className="fs-075 text-slate-500">Goes to: {t.recipient}</div>
      )}
    </div>
    <div className="form-check form-switch m-0">
      <input
        id={`ns-${t.type}`}
        type="checkbox"
        role="switch"
        className="form-check-input"
        checked={on}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={`${t.label || t.type}: ${on ? "on" : "off"}`}
      />
    </div>
  </li>
);

/**
 * Administration → Notification Settings: which notifications a company sends,
 * and the probation alerts. Follows the Approval Setup pattern: a company
 * without its own setting inherits its parent's (or the global default).
 */
const NotificationSettingsPage = () => {
  const user = useCurrentUser();
  const { companyWide, superAdmin } = usePermissions();
  const canEdit = companyWide("company-setup", "modify");
  const { data: companies = EMPTY } = useCompanies();
  const [companyId, setCompanyId] = useState(
    user.companyId || (superAdmin ? "global" : ""),
  );
  const catalog = useNotificationCatalog();
  const settings = useNotificationSettings(companyId, {
    enabled: !!companyId,
  });
  const save = useSaveNotificationSettings();
  const reset = useResetNotificationSettings();
  const busy = save.isPending || reset.isPending;

  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState([]);
  const loaded = settings.data?.config;
  // A fresh copy whenever the company or the saved setting changes
  useEffect(() => {
    setDraft(loaded ? structuredClone(loaded) : null);
    setErrors([]);
  }, [loaded, companyId]);

  const groups = useMemo(
    () => groupTypes(catalog.data?.types || EMPTY),
    [catalog.data],
  );
  const dirty =
    !!draft && !!loaded && JSON.stringify(draft) !== JSON.stringify(loaded);
  const source = SOURCE_LABEL[settings.data?.source] || null;
  const probation = draft?.probation || {};
  const setProbation = (changes) =>
    setDraft((d) => ({ ...d, probation: { ...d.probation, ...changes } }));
  const disabled = !canEdit || busy;

  const doSave = () => {
    const problems = probationProblems(draft.probation);
    if (problems.length) return setErrors(problems);
    setErrors([]);
    save.mutate(
      { companyId, config: toPayload(draft) },
      {
        onSuccess: () => toast.success("Notification settings saved."),
        onError: (err) =>
          setErrors(messagesOf(err, "Could not save the settings.")),
      },
    );
  };
  const doReset = () => {
    if (
      !window.confirm(
        "Remove this company's own settings? It goes back to its parent company's settings or the default.",
      )
    )
      return;
    reset.mutate(companyId, {
      onSuccess: () => toast.success("Back to the inherited settings."),
      onError: (err) =>
        toast.error(getApiErrorMessage(err, "Could not reset the settings.")),
    });
  };

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <PageHeader
          back={{ to: "/admin", label: "Administration" }}
          title="Notification Settings"
          subtitle="Which notifications this company sends, and the probation alerts."
        />

        <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
          <label htmlFor="ns-company" className="fs-080 text-slate-600">
            Company
          </label>
          <select
            id="ns-company"
            className="status-select"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            disabled={dirty}
            title={dirty ? "Save or discard your changes first" : undefined}
          >
            {!companyId && <option value="">Choose a company…</option>}
            {superAdmin && (
              <option value="global">Global default (every company)</option>
            )}
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.companyName || c.name}
              </option>
            ))}
          </select>
          {source && (
            <span className={`status-pill status-pill--${source.tone}`}>
              {settings.data?.source === "INHERITED" &&
              settings.data?.inheritedFrom
                ? `Inherited from ${settings.data.inheritedFrom.companyName}`
                : source.label}
            </span>
          )}
        </div>

        {!companyId ? (
          <div className="admin-empty">
            <i className="bi bi-buildings"></i>Choose a company first.
          </div>
        ) : settings.isLoading || catalog.isLoading ? (
          <div className="text-center py-5">
            <span
              className="spinner-border text-primary"
              role="status"
              aria-label="Loading"
            ></span>
          </div>
        ) : settings.isError || catalog.isError ? (
          <div className="alert alert-danger fs-085">
            {getApiErrorMessage(
              settings.error || catalog.error,
              "Could not load the notification settings.",
            )}
          </div>
        ) : (
          draft && (
            <>
              {settings.data?.source !== "OWN" && canEdit && (
                <div className="alert alert-info fs-080 py-2">
                  <i className="bi bi-info-circle me-1"></i>
                  {companyId === "global"
                    ? "These apply to every company without its own settings."
                    : "This company uses inherited settings. Change anything and save to give it its own."}
                </div>
              )}
              {errors.length > 0 && (
                <div className="alert alert-danger fs-085 py-2">
                  <ul className="mb-0 ps-3">
                    {errors.map((m) => (
                      <li key={m}>{m}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="row g-3">
                {groups.map((g) => (
                  <div key={g.group} className="col-lg-6 col-xxl-4">
                    <section className="admin-card h-100">
                      <h2 className="admin-card-title">{g.group}</h2>
                      <ul className="ns-list">
                        {g.types.map((t) => (
                          <TypeRow
                            key={t.type}
                            t={t}
                            on={isEnabled(draft, t.type)}
                            disabled={disabled}
                            onChange={(on) =>
                              setDraft((d) => setEnabled(d, t.type, on))
                            }
                          />
                        ))}
                      </ul>
                    </section>
                  </div>
                ))}

                <div className="col-lg-6 col-xxl-4">
                  <section className="admin-card h-100">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h2 className="admin-card-title m-0">
                        <i className="bi bi-hourglass-split"></i>Probation
                        alerts
                      </h2>
                      <div className="form-check form-switch m-0">
                        <input
                          id="ns-probation"
                          type="checkbox"
                          role="switch"
                          className="form-check-input"
                          checked={!!probation.enabled}
                          disabled={disabled}
                          onChange={(e) =>
                            setProbation({ enabled: e.target.checked })
                          }
                          aria-label="Probation alerts"
                        />
                      </div>
                    </div>
                    <p className="fs-075 text-slate-500">
                      A reminder before an employee reaches each chosen month of
                      service, so their probation can be reviewed.
                    </p>
                    <fieldset disabled={disabled || !probation.enabled}>
                      <div className="profile-label">Months of service</div>
                      <div
                        className="d-flex flex-wrap gap-1 mb-3"
                        role="group"
                        aria-label="Months"
                      >
                        {MONTHS.map((m) => {
                          const on = (probation.months || []).includes(m);
                          return (
                            <button
                              key={m}
                              type="button"
                              className={`btn btn-sm ${on ? "btn-primary" : "btn-light border"}`}
                              aria-pressed={on}
                              onClick={() =>
                                setProbation({
                                  months: toggleIn(probation.months, m),
                                })
                              }
                            >
                              {m}
                            </button>
                          );
                        })}
                      </div>
                      <label htmlFor="ns-days" className="profile-label">
                        Days before
                      </label>
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <input
                          id="ns-days"
                          type="number"
                          min={0}
                          max={60}
                          className="form-control form-control-sm w-auto"
                          value={probation.daysBefore ?? 0}
                          onChange={(e) =>
                            setProbation({ daysBefore: e.target.value })
                          }
                        />
                        <span className="fs-075 text-slate-500">
                          {Number(probation.daysBefore) === 0
                            ? "On the day itself"
                            : `Warn ${probation.daysBefore} day(s) before each month`}
                        </span>
                      </div>
                      <div className="profile-label">Who gets them</div>
                      {(
                        catalog.data?.probationRecipients || [
                          "HR",
                          "BRANCH_MANAGER",
                        ]
                      ).map((r) => (
                        <div key={r} className="form-check">
                          <input
                            id={`ns-rec-${r}`}
                            type="checkbox"
                            className="form-check-input"
                            checked={(probation.recipients || []).includes(r)}
                            onChange={() =>
                              setProbation({
                                recipients: toggleIn(probation.recipients, r),
                              })
                            }
                          />
                          <label
                            htmlFor={`ns-rec-${r}`}
                            className="form-check-label fs-085"
                          >
                            {RECIPIENT_LABEL[r] || r}
                          </label>
                        </div>
                      ))}
                    </fieldset>
                  </section>
                </div>
              </div>

              {canEdit && (
                <div className="ns-actions">
                  {settings.data?.source === "OWN" &&
                    companyId !== "global" && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary profile-btn me-auto"
                        onClick={doReset}
                        disabled={busy || dirty}
                      >
                        Use parent/default
                      </button>
                    )}
                  {dirty && (
                    <span className="fs-080 text-slate-600">
                      Unsaved changes
                    </span>
                  )}
                  <button
                    type="button"
                    className="btn btn-outline-secondary profile-btn"
                    onClick={() => {
                      setDraft(structuredClone(loaded));
                      setErrors([]);
                    }}
                    disabled={!dirty || busy}
                  >
                    Discard
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary profile-btn"
                    onClick={doSave}
                    disabled={!dirty || busy}
                  >
                    {save.isPending && (
                      <span
                        className="spinner-border spinner-border-sm"
                        aria-hidden="true"
                      ></span>
                    )}
                    Save
                  </button>
                </div>
              )}
            </>
          )
        )}
      </div>
    </Layout>
  );
};

export default NotificationSettingsPage;
